/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import cn from "classnames";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { setActiveFieldName } from "data/store/reducers/composer";
import { selectComposerActiveFieldName } from "data/store/reducers/composer";
import { setFieldOpen } from "data/store/reducers/composer";
import { useModel } from "cms/models/context/use-model";
import { useFieldZone } from "cms/fields/hooks/use-field-zone";
import { fieldsWithoutDefaults } from "data/resources/field-defaults";
import { useCurrentDynamicField } from "cms/fields/hooks/use-current-dynamic-field";
import { validateAllTrue } from "cms/fields/transformers/initialize-field";
import { AccordionBottomEdgeToggle } from "cms/fields/layout/footer/accordion-bottom-edge-toggle";
import { FieldEditorButtons } from "cms/fields/layout/footer/field-editor-buttons";
import { FieldEditorHeader } from "cms/fields/layout/header/field-editor-header";
import { useComposerMode } from "cms/components/hooks/use-composer-mode";
import { useSelectFieldSchemaProperty } from "cms/fields/hooks/use-select-field-schema-property";
import { useFieldOpen } from "cms/fields/hooks/use-field-open";

const Field = (props) => {

    const displayComponent = props?.displayComponent;
    const editComponent = props?.editComponent;
    const { ModelState } = useModel();
    const { field } = useCurrentDynamicField();
    const dispatch = useDispatch();
    const allCollapsed = ModelState.allCollapsed;
    const { editMode, contentOnly } = useComposerMode();
    const { FieldZoneState, initZoneField } = useFieldZone();
    const preview = FieldZoneState?.preview;
    const expanded = FieldZoneState?.expanded;
    const name = field?.name;
    const type = field?.type;
    const values = field?.values;
    const { getNamedField, getNamedFieldIsOpen } = useFieldOpen(name);
    const open = getNamedFieldIsOpen(name);
    const activeFieldName = useSelector(selectComposerActiveFieldName);
    const isActiveField = name === activeFieldName;
    const isComponentField = type === "field.component";
    const isExpanded = editMode ? open : true;
    const schemaSettings = useSelectFieldSchemaProperty(name,"settings");
    const defaultValue = schemaSettings?.defaultValue;
    const defaultStyle = schemaSettings?.defaultStyle;
    const defaultFormat = schemaSettings?.defaultFormat;
    const noOverride = schemaSettings?.noOverride;
    
    const handleHeaderClick = (event) => {
        event.stopPropagation();
        if (!isActiveField) {
            dispatch(setActiveFieldName(name));
        }
        const field = getNamedField(name);
        dispatch(setFieldOpen(field));
    };
    
    useEffect(() => {
        initZoneField("expanded", !allCollapsed);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allCollapsed]);

    useEffect(() => {
        const isWithoutDefaults = fieldsWithoutDefaults.includes(type);
        const hasDefaultValue = defaultValue != null; // one default item for entire list
        const hasDefaultStyle = defaultStyle != null;

        // If defaults are missing, we can not use defaults
        const dflags = [!isWithoutDefaults, hasDefaultValue, hasDefaultStyle];
        const canUseDefaults = validateAllTrue(dflags);

        let wasAlreadyDefault = true; // for entire list
        for (const item of values) {
            const value = item?.value;
            const settings = item?.settings;
            const style = settings?.style;
            const format = settings?.format;
            const isDefaultValue = defaultValue === value;
            const isDefaultStyle = defaultStyle === style;
            const isDefaultFormat = defaultFormat === format;
            const flags = [wasAlreadyDefault, isDefaultValue, isDefaultStyle, isDefaultFormat];
            wasAlreadyDefault = validateAllTrue(flags);
        }
        const disabled = noOverride || wasAlreadyDefault;
        initZoneField("canUseDefaults", canUseDefaults);
        initZoneField("wasAlreadyDefault", wasAlreadyDefault);
        initZoneField("disabled", disabled);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field]);

    const previewStyle = (preview || contentOnly) ? "preview" : "";
    const containerClassName = cn(
        "simplicity-composer-container",
        "simplicity-composer-fields-container",
        previewStyle,
        { "is-active-field": editMode && isActiveField }
    );

    return (
        <>

            {/*  FIXME: using "display: none" to have editor elements in a display page is not acceptable  */}

            <div className={containerClassName}>
                <div className='simplicity-composer'>
                    <Accordion 
                        className='simplicity-composer-accordion-root'
                        expanded={isExpanded}
                    >
                        <div className={`simplicity-composer-header field-header ${!expanded ? "closed" : ""}`}>
                            <AccordionSummary
                                id={`component${name}_header`}
                                className='simplicity-composer-accordion-header'
                                aria-controls='simplicity-composer-body'
                                onClick={handleHeaderClick}
                            >
                                <FieldEditorHeader 
                                    isActiveField={isActiveField}
                                    showActiveFieldSelector={isComponentField}
                                >
                                    <AccordionBottomEdgeToggle
                                        show={!open}
                                    />
                                </FieldEditorHeader>

                            </AccordionSummary>
                        </div>
                        <div className='simplicity-composer-body-main'>
    
                            {/* ----- dynamically include display / edit react component ----- */}
                            <AccordionDetails>
                                {contentOnly ? displayComponent : editComponent}
                            </AccordionDetails>
    
                            {editMode &&
                                <FieldEditorButtons />
                            }
                            <AccordionBottomEdgeToggle show={open} />
                        </div>
                    </Accordion>
                </div>
            </div>
        </>
    );
};

export { Field };
